import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2,
  LogOut,
  Inbox,
  ShieldAlert,
  ArrowLeft,
  Mail,
  Phone,
  LayoutTemplate,
} from "lucide-react";
import { SITE } from "../config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4.5 w-4.5 h-[18px] w-[18px]" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.3-2.1 3.7-5.2 3.7-8.6z" />
    <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.8-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.1 1.2-3.1 0-5.8-2.1-6.8-5H1.4v3C3.3 21.3 7.3 24 12 24z" />
    <path fill="#FBBC05" d="M5.2 14.4c-.2-.7-.4-1.5-.4-2.4s.2-1.7.4-2.4v-3H1.4C.5 8.4 0 10.1 0 12s.5 3.6 1.4 5.4l3.8-3z" />
    <path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 3.3 2.7 1.4 6.6l3.8 3c1-2.9 3.7-4.9 6.8-4.9z" />
  </svg>
);

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export default function Admin() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [messages, setMessages] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (user) return;
    axios
      .get(`${API}/auth/me`)
      .then((res) => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, [user]);

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/contact`)
      .then((res) => setMessages(res.data))
      .catch((err) => {
        if (err?.response?.status === 403) setDenied(true);
        else setMessages([]);
      });
  }, [user]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      /* clear locally regardless */
    }
    setUser(null);
    setMessages(null);
    setDenied(false);
  };

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#05060a]" data-testid="admin-checking">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-indigo-300" />
          <p className="mt-4 font-mono text-xs uppercase tracking-[0.3em] text-slate-500">
            Checking session…
          </p>
        </div>
      </div>
    );
  }

  /* ---------- Not signed in ---------- */
  if (!user) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#05060a] px-6" data-testid="admin-signin-page">
        <div className="pointer-events-none absolute left-1/2 top-1/3 h-[380px] w-[560px] -translate-x-1/2 rounded-full bg-indigo-600/[0.09] blur-[120px]" aria-hidden="true" />
        <div className="glass-strong relative w-full max-w-md rounded-3xl p-9 text-center" data-testid="admin-signin-card">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 font-display text-base font-extrabold text-white shadow-[0_0_22px_rgba(129,140,248,0.5)]">
            GD
          </span>
          <h1 className="mt-5 font-display text-2xl font-bold text-white">Admin Access</h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-400">
            Sign in with Google to view project enquiries submitted through your
            website.
          </p>
          <button
            onClick={login}
            data-testid="admin-signin-button"
            className="mt-7 inline-flex w-full items-center justify-center gap-3 rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-slate-800 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(255,255,255,0.25)]"
          >
            <GoogleIcon />
            Sign in with Google
          </button>
          <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-600">
            Access limited to {SITE.email}
          </p>
        </div>
        <Link
          to="/"
          data-testid="admin-back-to-site"
          className="mt-8 inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" /> Back to website
        </Link>
      </div>
    );
  }

  /* ---------- Signed in but not the owner ---------- */
  if (denied) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#05060a] px-6" data-testid="admin-denied-page">
        <div className="glass-strong w-full max-w-md rounded-3xl p-9 text-center" data-testid="admin-denied-card">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-rose-400/30 bg-rose-500/10">
            <ShieldAlert className="h-6 w-6 text-rose-400" />
          </span>
          <h1 className="mt-5 font-display text-2xl font-bold text-white">Access restricted</h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-400">
            You are signed in as{" "}
            <span className="font-semibold text-slate-200">{user.email}</span>.
            This area is limited to Govind Developer&apos;s account.
          </p>
          <div className="mt-7 flex flex-col gap-3">
            <button
              onClick={logout}
              data-testid="admin-denied-logout"
              className="btn-ghost inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white"
            >
              <LogOut className="h-4 w-4" /> Switch account
            </button>
            <Link
              to="/"
              data-testid="admin-denied-back"
              className="inline-flex items-center justify-center gap-2 text-sm text-slate-400 transition-colors hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" /> Back to website
            </Link>
          </div>
        </div>
      </div>
    );
  }

  /* ---------- Owner dashboard ---------- */
  return (
    <div className="relative min-h-screen bg-[#05060a] text-slate-100" data-testid="admin-dashboard">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[340px] w-[760px] -translate-x-1/2 rounded-full bg-indigo-600/[0.08] blur-[120px]" aria-hidden="true" />

      <header className="border-b border-white/[0.07] bg-[#05060a]/80 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-6xl items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="admin-logo" className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 font-display text-sm font-extrabold text-white shadow-[0_0_18px_rgba(129,140,248,0.5)]">
              GD
            </span>
            <span className="font-display text-[15px] font-bold text-white sm:text-base">
              Govind <span className="text-gradient">Developer</span>
            </span>
            <span className="ml-2 hidden rounded-full border border-indigo-400/30 bg-indigo-500/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.24em] text-indigo-300 sm:inline-block">
              Admin
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <span
              className="glass hidden items-center gap-2 rounded-full px-4 py-2 text-xs font-medium text-slate-300 sm:flex"
              data-testid="admin-user-chip"
            >
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              {user.email}
            </span>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="btn-ghost inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="relative mx-auto max-w-6xl px-5 py-12 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="flex items-center gap-3 font-mono text-[11px] font-medium uppercase tracking-[0.3em] text-indigo-300/90">
              <span className="h-px w-8 bg-gradient-to-r from-indigo-400 to-transparent" />
              Project Enquiries
            </p>
            <h1 className="mt-3 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Contact <span className="text-gradient">Messages</span>
            </h1>
          </div>
          <span
            className="glass inline-flex items-center gap-2 rounded-full px-4 py-2 font-mono text-xs text-slate-300"
            data-testid="admin-message-count"
          >
            <Inbox className="h-3.5 w-3.5 text-indigo-300" />
            {messages === null ? "…" : `${messages.length} total`}
          </span>
        </div>

        {messages === null ? (
          <div className="mt-16 text-center" data-testid="admin-loading">
            <Loader2 className="mx-auto h-7 w-7 animate-spin text-indigo-300" />
          </div>
        ) : messages.length === 0 ? (
          <div className="glass mt-10 rounded-3xl p-12 text-center" data-testid="admin-empty-state">
            <Inbox className="mx-auto h-9 w-9 text-slate-500" />
            <p className="mt-4 font-display text-lg font-bold text-white">No enquiries yet</p>
            <p className="mx-auto mt-2 max-w-sm text-sm text-slate-400">
              When a client fills the “Start a Project” form on your website,
              their message will appear here.
            </p>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {messages.map((m, i) => (
              <article
                key={m.id || i}
                className="glass card-hover rounded-2xl p-6"
                data-testid={`admin-message-card-${i + 1}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display text-lg font-bold text-white">{m.name}</h3>
                    <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      {fmtDate(m.created_at)}
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-indigo-400/25 bg-indigo-500/10 px-3 py-1 text-[11px] font-semibold text-indigo-300">
                    <LayoutTemplate className="h-3 w-3" />
                    {m.website_type}
                  </span>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-300">{m.message}</p>
                <div className="mt-5 flex flex-wrap gap-4 border-t border-white/[0.06] pt-4 text-xs">
                  <a
                    href={`mailto:${m.email}`}
                    data-testid={`admin-message-email-${i + 1}`}
                    className="inline-flex items-center gap-1.5 text-slate-400 transition-colors hover:text-white"
                  >
                    <Mail className="h-3.5 w-3.5 text-indigo-300" /> {m.email}
                  </a>
                  <a
                    href={`tel:${m.phone}`}
                    data-testid={`admin-message-phone-${i + 1}`}
                    className="inline-flex items-center gap-1.5 text-slate-400 transition-colors hover:text-white"
                  >
                    <Phone className="h-3.5 w-3.5 text-indigo-300" /> {m.phone}
                  </a>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
